import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

interface DealerApplication {
  id: string;
  jotform_submission_id: string;
  status: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  business_type: string;
  created_at: string;
  updated_at: string;
  resale_certificate_url: string | null;
  out_of_state_cert_url?: string | null;
  w9_url: string | null;
  federal_id_ein: string | null;
  drivers_license_number: string | null;
  billing_address: any;
  info_request_message: string | null;
  territory_name?: string | null;
  sales_rep_name?: string | null;
  referring_rep_firm_name?: string | null;
  in_quickbooks?: boolean;
  dealer_fully_approved?: boolean;
  created_dealer_id?: string | null;
}

/**
 * Compute a smart label for portal_granted rows so accountants see what's
 * ACTUALLY pending, not just a generic "Docs Needed":
 *   - Missing core docs (W-9 / resale / EIN / address) → "Docs Needed"
 *   - Docs complete, not in QB yet                     → "Needs QB Entry"
 *   - Docs complete + in QB, awaiting final approval   → "Ready to Approve"
 * Any non-portal_granted status uses the static map.
 */
function getStatusDisplay(app: DealerApplication): { label: string; color: string } {
  const baseLabels: Record<string, string> = {
    pending: 'Pending', approved: 'Approved', rejected: 'Rejected',
    on_hold: 'On Hold', dismissed: 'Dismissed', info_requested: 'Info Requested',
    portal_granted: 'Portal Active — Needs Review',
  };
  const baseColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    on_hold: 'bg-red-100 text-red-800',
    dismissed: 'bg-gray-100 text-gray-600',
    info_requested: 'bg-blue-100 text-blue-800',
    portal_granted: 'bg-indigo-100 text-indigo-800',
  };
  if (app.status !== 'portal_granted') {
    return { label: baseLabels[app.status] || app.status, color: baseColors[app.status] || 'bg-gray-100 text-gray-800' };
  }
  // portal_granted = accounting must finish the approval. Frame every sub-state
  // as a REVIEW action (not "docs needed", which reads as wait-on-the-dealer and
  // got these rows ignored).
  const { score, total } = getComplianceInfo(app);
  if (score < total) return { label: 'Review — Docs Incomplete', color: 'bg-amber-100 text-amber-800' };
  if (!app.in_quickbooks) return { label: 'Review — Add to QB', color: 'bg-purple-100 text-purple-800' };
  return { label: 'Review — Ready to Approve', color: 'bg-emerald-100 text-emerald-800' };
}

function getComplianceInfo(app: DealerApplication) {
  // The current combined JotForm GENERATES the W-9 + resale/exempt cert PDFs from
  // the form fields (stored on billing_address) — it does NOT upload file URLs.
  // So a doc counts as present if EITHER the generated form data OR an uploaded
  // file exists. (Checking only file URLs falsely flagged form-generated docs as
  // "incomplete", so accounting skipped ready-to-approve dealers.)
  const ba = app.billing_address || {};
  const hasResale = !!app.resale_certificate_url || !!app.out_of_state_cert_url
    || !!(ba.txPermitNumber || ba.outOfStatePermitNumber);
  const hasW9 = !!app.w9_url || !!(ba.w9Name || ba.w9BusinessName || ba.w9TaxClassification);
  const hasEin = !!app.federal_id_ein;
  const hasAddress = !!(ba.street && ba.city && ba.state);

  let flaggedItems: string[] = [];
  if (app.status === 'info_requested' && app.info_request_message) {
    try {
      const parsed = JSON.parse(app.info_request_message);
      flaggedItems = parsed.flaggedItems || [];
    } catch { /* legacy string */ }
  }

  const items = [
    { done: hasResale, flagged: flaggedItems.includes('resale_cert') },
    { done: hasW9, flagged: flaggedItems.includes('w9') },
    { done: hasEin, flagged: flaggedItems.includes('ein') },
    { done: hasAddress, flagged: flaggedItems.includes('address') },
  ];
  const score = items.filter(i => i.done && !i.flagged).length;
  const flaggedCount = items.filter(i => i.flagged).length;
  return { score, total: 4, flaggedCount, items };
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  on_hold: 'bg-red-100 text-red-800',
  dismissed: 'bg-gray-100 text-gray-600',
  info_requested: 'bg-blue-100 text-blue-800',
  portal_granted: 'bg-indigo-100 text-indigo-800',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  on_hold: 'On Hold',
  dismissed: 'Dismissed',
  info_requested: 'Info Requested',
  portal_granted: 'Portal Active — Needs Review',
};

// Tabs so accounting sees each category instead of one blended list. "Needs
// Action" groups everything awaiting an accounting decision (incl. portal_granted,
// which used to hide among 100+ approved rows and got missed).
const APP_TABS: { key: string; label: string; statuses: string }[] = [
  { key: 'needs_action', label: 'Needs Action', statuses: 'pending,info_requested,on_hold,portal_granted' },
  { key: 'interest', label: 'Interest', statuses: 'interest' },
  { key: 'approved_interest', label: 'Approved Interest', statuses: 'approved_interest' },
  { key: 'approved', label: 'Approved', statuses: 'approved' },
  { key: 'dismissed', label: 'Dismissed', statuses: 'dismissed,rejected' },
  { key: 'all', label: 'All', statuses: '' },
];

function tabCount(key: string, counts: Record<string, number>): number {
  const tab = APP_TABS.find(t => t.key === key);
  if (!tab || !tab.statuses) return Object.values(counts).reduce((a, b) => a + b, 0);
  return tab.statuses.split(',').reduce((sum, s) => sum + (counts[s] || 0), 0);
}

export default function DealerApplicationsPage() {
  const [applications, setApplications] = useState<DealerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('needs_action');
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [interestActionId, setInterestActionId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search]);

  useEffect(() => {
    fetchApplications();
  }, [activeTab, search, page]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const tab = APP_TABS.find(t => t.key === activeTab);
      const data = await api.accounting.getPendingApplications({
        status: tab?.statuses || undefined,
        search: search || undefined,
        page,
        limit: 50,
      });
      setApplications(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      if (data.statusCounts) setStatusCounts(data.statusCounts);
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (app: DealerApplication) => {
    if (!confirm(`Delete application for "${app.company_name || app.email}"? This cannot be undone.`)) return;
    try {
      await api.accounting.deleteDealerApplication(app.id);
      setApplications(prev => prev.filter(a => a.id !== app.id));
      setTotal(prev => prev - 1);
    } catch (err: any) {
      alert('Failed to delete: ' + (err.message || 'Unknown error'));
    }
  };

  const handleApproveInterest = async (app: DealerApplication) => {
    if (!confirm(`Approve "${app.company_name || app.email}"? This emails them the full dealer application to complete.`)) return;
    setInterestActionId(app.id);
    try {
      await api.accounting.approveInterest(app.id);
      await fetchApplications();
      alert('Approved — the full application was emailed to the applicant.');
    } catch (err: any) {
      alert('Failed to approve: ' + (err.message || 'Unknown error'));
    } finally {
      setInterestActionId(null);
    }
  };

  const handleDenyInterest = async (app: DealerApplication) => {
    if (!confirm(`Deny "${app.company_name || app.email}"?`)) return;
    setInterestActionId(app.id);
    try {
      await api.accounting.denyInterest(app.id);
      await fetchApplications();
    } catch (err: any) {
      alert('Failed to deny: ' + (err.message || 'Unknown error'));
    } finally {
      setInterestActionId(null);
    }
  };

  const handleResendApplication = async (app: DealerApplication) => {
    if (!confirm(`Resend the dealer application email to "${app.company_name || app.email}"?`)) return;
    setInterestActionId(app.id);
    try {
      const res = await api.accounting.resendApplication(app.id);
      alert(res?.message || 'Application re-sent.');
    } catch (err: any) {
      alert('Failed to resend: ' + (err.message || 'Unknown error'));
    } finally {
      setInterestActionId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysWaiting = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dealer Applications</h1>
        <p className="text-gray-500 mt-1">Review and approve new dealer applications from Jotform ({total} total)</p>
      </div>

      {/* Tabs — separate each category so nothing gets missed */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex flex-wrap gap-1">
          {APP_TABS.map((t) => {
            const count = tabCount(t.key, statusCounts);
            const active = activeTab === t.key;
            const isAction = t.key === 'needs_action';
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span
                    className={`ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                      isAction ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <input
          type="text"
          placeholder="Search by company, contact, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && applications.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-500">No applications found</p>
        </div>
      )}

      {/* Applications List */}
      {!loading && !error && applications.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.map((app) => {
                const daysWaiting = getDaysWaiting(app.created_at);
                return (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{app.company_name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">
                        {app.business_type || ''}
                        {app.territory_name && (
                          <span className="ml-1 text-xs text-blue-600">{app.territory_name}</span>
                        )}
                        {app.referring_rep_firm_name && (
                          <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{app.referring_rep_firm_name}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{app.contact_name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{app.email || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const c = getComplianceInfo(app);
                        const labels = ['Resale', 'W-9', 'EIN', 'Address'];
                        if (c.score === c.total && c.flaggedCount === 0) {
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                              Complete
                            </span>
                          );
                        }
                        if (c.flaggedCount > 0) {
                          return (
                            <div className="flex flex-wrap gap-1">
                              {c.items.map((item, i) => (
                                item.flagged ? (
                                  <span key={i} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700">{labels[i]}</span>
                                ) : item.done ? (
                                  <span key={i} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-700">{labels[i]}</span>
                                ) : (
                                  <span key={i} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-400">{labels[i]}</span>
                                )
                              ))}
                            </div>
                          );
                        }
                        return (
                          <div className="flex flex-wrap gap-1">
                            {c.items.map((item, i) => (
                              <span key={i} className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded ${item.done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>{labels[i]}</span>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const sd = getStatusDisplay(app);
                        return (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${sd.color}`}>
                            {sd.label}
                          </span>
                        );
                      })()}
                      {app.status === 'pending' && daysWaiting > 1 && (
                        <span className="ml-2 text-xs text-orange-600">
                          {daysWaiting} days
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(app.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {app.status === 'interest' && (
                          <>
                            <button
                              onClick={() => handleApproveInterest(app)}
                              disabled={interestActionId === app.id}
                              className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50"
                              title="Approve → emails the applicant the full dealer application"
                            >
                              {interestActionId === app.id ? '…' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handleDenyInterest(app)}
                              disabled={interestActionId === app.id}
                              className="px-3 py-1 border border-red-300 text-red-600 text-xs font-medium rounded hover:bg-red-50 disabled:opacity-50"
                            >
                              Deny
                            </button>
                          </>
                        )}
                        {app.status === 'approved_interest' && (
                          <button
                            onClick={() => handleResendApplication(app)}
                            disabled={interestActionId === app.id}
                            className="px-3 py-1 border border-blue-300 text-blue-600 text-xs font-medium rounded hover:bg-blue-50 disabled:opacity-50"
                            title="Re-email the dealer application link to the applicant"
                          >
                            {interestActionId === app.id ? '…' : 'Resend Application'}
                          </button>
                        )}
                        <Link
                          to={`/accounting/approvals/dealers/${app.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          Review
                        </Link>
                        <button
                          onClick={() => handleDelete(app)}
                          className="text-red-400 hover:text-red-600 p-1"
                          title="Delete application"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages} ({total} applications)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
